import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ChartData, ChartConfiguration } from 'chart.js';
import {
  ChartDataPoint,
  AggregatedData,
  UrlState,
  LogAnalyticsResult
} from '../models/chart-interfaces';
import { OwnershipEntry } from '../../../models/ownership-entry';
import { TeamOwnerMinorEntry } from '../../../models/team-owner-minor-entry';

@Injectable({
  providedIn: 'root'
})
export class ChartDataTransformService {

  /**
   * Transform raw alert and ownership data into unified ChartDataPoints
   */
  transformData(
    alertData: LogAnalyticsResult,
    ownershipData: OwnershipEntry[],
    teamData: TeamOwnerMinorEntry[]
  ): Observable<ChartDataPoint[]> {
    try {
      const chartDataPoints: ChartDataPoint[] = [];

      // Handle null/undefined inputs
      if (!alertData || !ownershipData) {
        return of([]);
      }

      // Create dual lookup maps for ownership data
      const repoOwnershipMap = new Map<string, OwnershipEntry>();
      const teamReposMap = new Map<string, string[]>();

      ownershipData.forEach(entry => {
        // Repository-first mapping for direct lookups
        repoOwnershipMap.set(entry.repository.toLowerCase(), entry);

        // Team-to-repositories mapping for alert processing
        const teamKey = entry.team.toLowerCase();
        if (!teamReposMap.has(teamKey)) {
          teamReposMap.set(teamKey, []);
        }
        teamReposMap.get(teamKey)!.push(entry.repository);
      });

      // Process alert data from Log Analytics
      if (alertData.tables && alertData.tables.length > 0) {
        const table = alertData.tables[0];
        const teamNameIndex = table.columns.findIndex(col => col.name === 'teamName');
        const alertNameIndex = table.columns.findIndex(col => col.name === 'alertName');
        const severityIndex = table.columns.findIndex(col => col.name === 'severity');
        const resourceGroupIndex = table.columns.findIndex(col => col.name === 'resourceGroup');
        const countIndex = table.columns.findIndex(col => col.name === 'count_');

        table.rows.forEach(row => {
          const teamName = row[teamNameIndex] as string;
          const alertName = row[alertNameIndex] as string;
          const alertCount = row[countIndex] as number;
          const severity = row[severityIndex] as string;
          const resourceGroup = row[resourceGroupIndex] as string;

          // Map resourceGroup to repository using ownership data
          let repository = 'No Resource Group';
          let ownershipEntry: OwnershipEntry | undefined;

          if (resourceGroup && resourceGroup.trim()) {
            // Find ownership entry that contains this resourceGroup in azureResourceGroups
            ownershipEntry = ownershipData.find(entry =>
              entry.azureResourceGroups &&
              entry.azureResourceGroups.includes(resourceGroup)
            );

            if (ownershipEntry) {
              repository = ownershipEntry.repository;
            }
          }

          // Create repository-level data point
          const dataPoint: ChartDataPoint = {
            alertName,
            alertCount,
            teamName,
            repository,
            resourceGroup,
            location: ownershipEntry?.location,
            admin: ownershipEntry?.admin,
            organizationName: ownershipEntry?.organization,
            projectName: ownershipEntry?.project,
            systems: ownershipEntry?.systems || [],
            severity: severity
          };

          chartDataPoints.push(dataPoint);
        });
      }

      return of(chartDataPoints);
    } catch (error) {
      console.error('Error transforming data:', error);
      return of([]);
    }
  }

  /**
   * Group and aggregate data based on groupBy field
   */
  groupAndAggregateData(
    dataPoints: ChartDataPoint[],
    groupBy: string,
    aggregateBy: string = 'count'
  ): AggregatedData[] {
    const groups = new Map<string, ChartDataPoint[]>();

    // Group data points
    dataPoints.forEach(point => {
      const groupKey = this.getGroupKey(point, groupBy);
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(point);
    });

    // Aggregate grouped data
    const aggregatedData: AggregatedData[] = [];
    groups.forEach((groupPoints, groupKey) => {
      const totalCount = this.aggregatePoints(groupPoints, aggregateBy);
      const uniqueAlerts = new Set(groupPoints.map(p => p.alertName)).size;

      aggregatedData.push({
        groupKey,
        groupLabel: this.getGroupLabel(groupKey, groupBy),
        totalCount,
        uniqueAlerts,
        dataPoints: groupPoints
      });
    });

    // Sort by total count descending
    return aggregatedData.sort((a, b) => b.totalCount - a.totalCount);
  }

  /**
   * Create Chart.js data structure
   */
  createChartData(aggregatedData: AggregatedData[], state: UrlState): ChartData<any> {

    switch (state.chartType) {
      case 'pie':
      case 'doughnut':
      case 'polarArea':
        return this.createPieChartData(aggregatedData);

      case 'line':
        return this.createLineChartData(aggregatedData);

      case 'bar':
      default:
        return this.createBarChartData(aggregatedData, state);
    }
  }

  /**
   * Create Chart.js options
   */
  createChartOptions(state: UrlState): ChartConfiguration<any>['options'] {
    const baseOptions: ChartConfiguration<any>['options'] = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: this.getChartTitle(state),
          font: { size: 16 }
        },
        legend: {
          display: state.chartOptions?.showLegend !== false,
          position: 'top'
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y || context.parsed;
              return `${label}: ${value} alerts`;
            }
          }
        }
      }
    };

    // Chart type specific options
    switch (state.chartType) {
      case 'bar':
        return {
          ...baseOptions,
          indexAxis: state.chartOptions?.orientation === 'horizontal' ? 'y' : 'x',
          scales: {
            x: {
              stacked: state.chartOptions?.stacked,
              title: { display: true, text: this.getAxisLabel(state.groupBy) }
            },
            y: {
              stacked: state.chartOptions?.stacked,
              beginAtZero: true,
              title: { display: true, text: 'Alert Count' }
            }
          }
        };


      case 'line':
        return {
          ...baseOptions,
          scales: {
            x: {
              title: { display: true, text: this.getAxisLabel(state.groupBy) }
            },
            y: {
              beginAtZero: true,
              title: { display: true, text: 'Alert Count' }
            }
          }
        };

      case 'pie':
      case 'doughnut':
      case 'polarArea':
        return {
          ...baseOptions,
          plugins: {
            ...baseOptions.plugins,
            tooltip: {
              callbacks: {
                label: (context: any) => {
                  const label = context.label || '';
                  const value = context.parsed;
                  const total = context.dataset.data.reduce((sum: number, val: number) => sum + val, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  return `${label}: ${value} alerts (${percentage}%)`;
                }
              }
            }
          }
        };

      default:
        return baseOptions;
    }
  }

  // Private helper methods

  private getGroupKey(point: ChartDataPoint, groupBy: string): string {
    switch (groupBy) {
      case 'team':
        return point.teamName;
      case 'repository':
        return point.repository || 'Unknown Repository';
      case 'alertType':
        return point.alertName;
      case 'location':
        return point.location || 'Unknown Location';
      case 'pipelineType':
        return point.pipelineType || 'No Pipeline';
      case 'admin':
        return point.admin || 'Unknown Admin';
      case 'systems':
        return point.systems?.join(', ') || 'No Systems';
      default:
        return point.teamName;
    }
  }

  private getGroupLabel(groupKey: string, groupBy: string): string {
    // Could add custom labeling logic here
    return groupKey;
  }

  private aggregatePoints(points: ChartDataPoint[], aggregateBy: string): number {
    switch (aggregateBy) {
      case 'count':
        return points.reduce((sum, point) => sum + point.alertCount, 0);
      case 'average':
        return points.reduce((sum, point) => sum + point.alertCount, 0) / points.length;
      case 'max':
        return Math.max(...points.map(point => point.alertCount));
      case 'min':
        return Math.min(...points.map(point => point.alertCount));
      default:
        return points.reduce((sum, point) => sum + point.alertCount, 0);
    }
  }

  private createBarChartData(aggregatedData: AggregatedData[], state: UrlState): ChartData<'bar'> {
    const labels = aggregatedData.map(data => data.groupLabel);
    const data = aggregatedData.map(data => data.totalCount);

    return {
      labels,
      datasets: [{
        label: 'Alert Count',
        data,
        backgroundColor: this.generateColors(aggregatedData.length, 0.6),
        borderColor: this.generateColors(aggregatedData.length, 1),
        borderWidth: 1
      }]
    };
  }

  private createLineChartData(aggregatedData: AggregatedData[]): ChartData<'line'> {
    const labels = aggregatedData.map(data => data.groupLabel);
    const data = aggregatedData.map(data => data.totalCount);

    return {
      labels,
      datasets: [{
        label: 'Alert Count',
        data,
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1
      }]
    };
  }

  private createPieChartData(aggregatedData: AggregatedData[]): ChartData<'pie'> {
    const labels = aggregatedData.map(data => data.groupLabel);
    const data = aggregatedData.map(data => data.totalCount);

    return {
      labels,
      datasets: [{
        data,
        backgroundColor: this.generateColors(aggregatedData.length, 0.8),
        borderColor: this.generateColors(aggregatedData.length, 1),
        borderWidth: 1
      }]
    };
  }

  private generateColors(count: number, alpha: number = 1): string[] {
    const colors = [
      `rgba(255, 99, 132, ${alpha})`,   // Red
      `rgba(54, 162, 235, ${alpha})`,   // Blue
      `rgba(255, 205, 86, ${alpha})`,   // Yellow
      `rgba(75, 192, 192, ${alpha})`,   // Green
      `rgba(153, 102, 255, ${alpha})`,  // Purple
      `rgba(255, 159, 64, ${alpha})`,   // Orange
      `rgba(199, 199, 199, ${alpha})`,  // Grey
      `rgba(83, 102, 255, ${alpha})`,   // Indigo
      `rgba(255, 99, 255, ${alpha})`,   // Pink
      `rgba(99, 255, 132, ${alpha})`    // Light Green
    ];

    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(colors[i % colors.length]);
    }
    return result;
  }

  private getChartTitle(state: UrlState): string {
    const groupByLabel = this.getGroupByLabel(state.groupBy);
    return `Alerts by ${groupByLabel} - ${this.getTimeRangeLabel(state.timeRange)}`;
  }

  private getGroupByLabel(groupBy: string): string {
    const labels: { [key: string]: string } = {
      'team': 'Team',
      'repository': 'Repository',
      'alertType': 'Alert Type',
      'location': 'Location',
      'pipelineType': 'Pipeline Type',
      'admin': 'Administrator',
      'systems': 'Systems'
    };
    return labels[groupBy] || groupBy;
  }

  private getAxisLabel(groupBy: string): string {
    return this.getGroupByLabel(groupBy);
  }

  private getTimeRangeLabel(timeRange: string): string {
    const labels: { [key: string]: string } = {
      'P7D': 'Last 7 Days',
      'P30D': 'Last 30 Days',
      'P90D': 'Last 90 Days',
      'P180D': 'Last 6 Months',
      'P365D': 'Last Year'
    };
    return labels[timeRange] || timeRange;
  }
}
